// src\themes\tolica\elements\Header\HeaderProfileLogin.tsx
'use client';

import Link from 'next/link';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { User, LayoutDashboard, ShieldCheck, LogOut, X } from 'lucide-react';
import Image from 'next/image';
import { useAuth } from '@/contexts/authContext';
import { useDictionary } from '@/core/hooks/use-dictionary';
import { useDrawerStore } from '@/stores/drawerStore';


export default function HeaderProfileLogin() {
    const { dictionary } = useDictionary()
    const { setPrimaryOpen, setSecondaryOpen, } = useDrawerStore();
    const { state, clearAuth, hasPermission } = useAuth();
    const isLoggedIn = state?.isAuthenticated;
    const user = state?.user;

    function handleClose() {
        setPrimaryOpen(false);
        setSecondaryOpen(false);
    }

    if (!isLoggedIn)
        return (
            <Link
                href="/login"
                onClick={handleClose}
                aria-label="ورود"
                className="text-sm tracking-wide p-2 rounded-xs bg-primary-100 w-1/2 hover:opacity-70 transition-opacity"
            >
                {dictionary.forms.login}
            </Link>
        );

    return (
        <Sheet>
            {/* Trigger */}
            <SheetTrigger asChild>
                <button
                    aria-label="User menu"
                    className="flex items-center bg-primary-100 rounded-xs p-2 w-1/2 gap-2 text-sm tracking-wide hover:opacity-70 transition-opacity"
                >
                    <User className="w-5 h-5 stroke-[1.5]" />
                    <span>{user?.full_name}</span>
                </button>
            </SheetTrigger>

            {/* Panel از پایین */}
            <SheetContent
                side="bottom"
                className="w-full rounded-none border-t border-neutral-200 bg-white text-black p-0 flex flex-col"
            >
                {/* Header */}
                <SheetHeader className="flex items-center justify-between flex-row p-4 border-b border-neutral-200">
                    <div className="flex items-center gap-3">
                        {user?.avatar ? (
                            <Image
                                src={user.avatar}
                                alt={user.full_name || 'avatar'}
                                width={40}
                                height={40}
                                className="w-10 h-10 rounded-full object-cover"
                            />
                        ) : (
                            user?.full_name
                        )}
                        <SheetTitle className="text-xs uppercase tracking-[0.2em] text-primary-900">
                            {user?.full_name}
                        </SheetTitle>
                    </div>

                </SheetHeader>

                {/* Navigation */}
                <nav className="flex-1 px-4 space-y-1 text-sm mb-4">
                    <Link
                        href="/profile"
                        onClick={handleClose}
                        className="flex items-center justify-between p-2 hover:bg-neutral-50"
                    >
                        <span>{dictionary.forms.profile}</span>
                        <User className="w-5 h-5 stroke-[1.2] text-neutral-600" />
                    </Link>

                    <Separator className="my-1 bg-neutral-200" />
                    <Link
                        href="/dashboard"
                        onClick={handleClose}
                        className="flex items-center justify-between p-2 hover:bg-neutral-50"
                    >
                        <span>{dictionary.dashboard.title}</span>
                        <LayoutDashboard className="w-5 h-5 stroke-[1.2] text-neutral-600" />
                    </Link>

                    {hasPermission('AccessToAdmin') && (
                        <>
                            <Separator className="my-1 bg-neutral-200" />
                            <Link
                                href="/admin"
                                onClick={handleClose}
                                className="flex items-center justify-between p-2 hover:bg-neutral-50"
                            >
                                <span>{dictionary.ui.profile.adminPanel}</span>
                                <ShieldCheck className="w-5 h-5 stroke-[1.2] text-neutral-600" />
                            </Link>
                        </>
                    )}
                    <>
                        <Separator className="my-1 bg-neutral-200" />
                        <button
                            onClick={() => {
                                clearAuth();
                                handleClose();
                            }}
                            className="flex items-center w-full justify-between p-2 hover:bg-red-50 text-red-500"
                        >
                            <span>{dictionary.common.logout}</span>
                            <LogOut className="w-5 h-5 stroke-[1.2]" />
                        </button>
                    </>
                </nav>
            </SheetContent>
        </Sheet>
    );
}